// REACT
import { useEffect, useState } from "react";

// MATERIAL UI
import { Alert, Checkbox, Grid, Stack, Tooltip } from "@mui/material";

// COMPONENTS
import Title from "../common/Title";
import { LoadingSpinner } from "./LoadingSpinner";
import { StyledContainedButtons } from "./styled-components/ContainedButton";

// ICONS
import BrandIconsNew from "./icons/BrandIconsNew";
import CompanyAccountIcon from "./icons/CompanyAccountIcon";

// STYLES
import StyledButton from "../dashboard/StyledButton";

// REDUX
import {
  setAccountSelection,
  setApplicationStep,
  useOnboarding,
} from "src/store/features/onboarding.slice";
import api from "src/store/features/api";
import { useAppDispatch } from "src/store/store";
import { AccountType } from "src/store/features/api/generated";

// UTILS
import { DvsDisclosure } from "./utils/DvsDisclosure";
import { isUkCountry } from "src/utils/isUkCountry";
import { useHistory } from "react-router-dom";
import useIgnoreBiometrics from "./utils/useIgnoreBiometrics";
import { useUserSlice } from "src/store/features/user.slice";
import usePermission from "src/utils/hooks/usePermission";
import AppFormLegalTerm from "./utils/AppFormLegalTerm";
import UkAppFormLegalTerm from "./utils/UkAppFormLegalTerm";

export const Label = (props: { text: string }) => {
  return <div className="label">{props.text}</div>;
};

export const AccountButton = (props: {
  icon: JSX.Element;
  accountType: AccountType;
  disabled?: boolean;
}) => {
  const { accountType: selectedAccount } = useOnboarding();
  const { icon, accountType, disabled } = props;
  const dispatch = useAppDispatch();
  return (
    <StyledContainedButtons
      fullWidth
      disabled={disabled}
      selected={selectedAccount === accountType}
      sx={{
        textTransform: "capitalize",
        "&:hover": { backgroundColor: "#F8F9F9" },
        "&.Mui-disabled": { opacity: 0.5 },
      }}
      onClick={() => {
        dispatch(setAccountSelection(accountType));
      }}>
      <div>{icon}</div>
      <div data-testid="account-selection-button-text">{accountType + " Account"}</div>
    </StyledContainedButtons>
  );
};

export default function SelectAccountType() {
  const [isDvsDisclosureAccepted, setIsDvsDisclosureAccepted] = useState(false);
  const { accountType: selectedAccount, applicationStep, ukOnboarding } = useOnboarding();
  const [ignoreBiometrics] = useIgnoreBiometrics();
  const { isGuest } = usePermission();
  const {
    kycSteps: { biometrics },
    address: { country },
  } = useUserSlice();
  const history = useHistory();
  const dispatch = useAppDispatch();

  const isCountryAustralia =
    country?.toLowerCase() === "australia" || country?.toLowerCase() === "au";
  const isUkUser = isUkCountry(country);

  const { data: hasIndividualAccount, isLoading } =
    api.useCheckIndividualTradingAccountByEmailQuery();

  const [acceptDvsDisclosure, { isLoading: isAcceptDvsDisclosureLoading }] =
    api.useDvsDisclosureMutation();

  const [submitUkAccountType] = api.useSubmitUkAccountTypeMutation();

  const isButtonDisabled =
    !selectedAccount ||
    (isCountryAustralia && !isDvsDisclosureAccepted) ||
    isAcceptDvsDisclosureLoading;

  const handleNext = async () => {
    if (isCountryAustralia) {
      if (!isDvsDisclosureAccepted) return;
      try {
        await acceptDvsDisclosure({
          dvsDisclosurePayloadDto: {
            isDvsDisclosureAccepted,
          },
        }).unwrap();
      } catch (error) {
        return;
      }
    }

    // Persist the choice as a pause-and-come-back checkpoint so a returning
    // UK user can resume the correct flow. Fire-and-forget — the selection
    // itself must not block on this call. Entity onboarding is not available
    // for UK clients, so the choice is always Individual.
    if (isUkUser && selectedAccount === "Individual") {
      submitUkAccountType({
        submitUkAccountTypeDto: {
          accountType: "INDIVIDUAL",
        },
      });
    }

    if (selectedAccount === "Individual") {
      if (isUkUser) {
        dispatch(setApplicationStep("ukRiskSummary"));
      } else if (ignoreBiometrics || biometrics) {
        dispatch(setApplicationStep("aboutYou"));
      } else {
        dispatch(setApplicationStep("onfidoVerify"));
      }
    }
    if (selectedAccount === "Entity" && !isUkUser) {
      history.push("/entity-onboarding");
    }
  };

  useEffect(() => {
    // Multi Account check
    // If user is not a guest and has an individual account, redirect to
    // entity-onboarding. Entity onboarding is not available for UK clients.
    if (!isGuest && hasIndividualAccount && !isUkUser) {
      history.push("/entity-onboarding");
    }
  }, [hasIndividualAccount, isGuest, isUkUser]);

  useEffect(() => {
    // A stale "Entity" selection can be restored from persisted state (e.g. a
    // resume checkpoint saved before entity onboarding was disabled for UK
    // clients) — clear it so the Next button can't route into entity onboarding.
    if (isUkUser && selectedAccount === "Entity") {
      dispatch(setAccountSelection(null));
    }
  }, [isUkUser, selectedAccount]);

  const AccountSelectionPhase1 = () => {
    return (
      <>
        <Title title={{ partB: "Select Account Type" }} />
        <Grid container spacing={1} mt={4}>
          {isUkUser && ukOnboarding.progressExpired && (
            <Grid item xs={12} paddingBottom={"16px"}>
              <Alert severity="warning">
                Your saved application progress expired because more than 10 days passed since
                you started, so your application has been reset.
              </Alert>
            </Grid>
          )}
          <Grid item xs={6} paddingBottom={0}>
            <AccountButton icon={<BrandIconsNew />} accountType="Individual" />
          </Grid>
          {/* Entity onboarding is not available for UK clients */}
          <Grid item xs={6} paddingBottom={"16px"}>
            {isUkUser ? (
              <Tooltip title="Temporarily unavailable">
                <span style={{ display: "block" }}>
                  <AccountButton icon={<CompanyAccountIcon />} accountType="Entity" disabled />
                </span>
              </Tooltip>
            ) : (
              <AccountButton icon={<CompanyAccountIcon />} accountType="Entity" />
            )}
          </Grid>
          {/* UK compliance feedback: no consent tick box, and the reworded
              legal term is always visible. Other regions keep the original
              behaviour below. */}
          {isUkUser ? (
            <UkAppFormLegalTerm />
          ) : (
            <>
              <Grid item xs={12} padding={"16px"} sx={{ alignSelf: "flex-start" }}>
                {isCountryAustralia && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Checkbox
                      checked={isDvsDisclosureAccepted}
                      onChange={(e) => setIsDvsDisclosureAccepted(e.target.checked)}
                      sx={{
                        "&:not(.Mui-checked)": {
                          color: "#000000",
                        },
                        "&.Mui-checked": {
                          color: "#FC4C4C",
                        },
                      }}
                    />
                    <DvsDisclosure />
                  </Stack>
                )}
              </Grid>
              {!isCountryAustralia && <AppFormLegalTerm />}
              {isCountryAustralia && isDvsDisclosureAccepted && <AppFormLegalTerm />}
            </>
          )}
          <Grid item xs={12} paddingBottom={"16px"}>
            <StyledButton
              disabled={isButtonDisabled}
              onClick={() => {
                handleNext();
              }}
              sx={{
                backgroundColor: "transparent",
                width: "100%",
              }}>
              Next
            </StyledButton>
          </Grid>
        </Grid>
      </>
    );
  };

  return (
    <>
      {isLoading ? (
        <Grid container display="flex" alignItems="center" justifyContent="center" height="70vh">
          <LoadingSpinner size={60} />
        </Grid>
      ) : (
        <>{applicationStep === "accountType" && <AccountSelectionPhase1 />}</>
      )}
      <div></div>
    </>
  );
}
